/**
 * Ambient light as a function of depth — issue #31, option 3.
 *
 * `#23` built the light field correctly and `#31` measured the consequence:
 * with `ambient` a single global 0.35 and almost nothing in the world emitting,
 * every cell of every biome rendered at 0.35× its palette colour and the game
 * was unplayable outside the two biomes that contain lava. The fix has two
 * halves and this file is the first one — **ambient is a property of where you
 * are**, not a constant the whole world shares. The second half is
 * `BiomePalette.glow`: authored emitters that carry each biome's identity, so
 * that lowering the floor buys drama instead of blindness.
 *
 * WHY A CURVE AND NOT A LOOKUP. A per-biome constant read at the camera's band
 * would step at every band line, and a step in ambient reads as a rendering
 * bug — the player sees the whole screen change brightness while walking. So
 * the biome values below are *control points* on a curve sampled continuously
 * in world y, and the only discontinuity in the game is the one the fiction
 * already has: the ground line, above which there is sky.
 *
 * WHY DEPTH DARKENS. It is free progression pressure (#31, and docs/05 §1 makes
 * the same argument about descent order). A light-producing weapon, a torch, a
 * burning oil slick and the Cathedral's radiant matter are all worth more the
 * deeper you are, without a single number in the combat layer changing.
 *
 * DETERMINISM. Pure functions of world y. Nothing here reads a clock, a camera
 * or a chunk, so two clients at the same depth agree, and a screenshot test can
 * assert the value at a coordinate.
 */

import { BAND_HEIGHT, SURFACE_Y } from './worldgen';
import { MAIN_PATH, biomeById } from './biomes';

/**
 * The surface at noon. 1.0 is "render exactly as the game did before the field
 * existed", which is the correct value for a band whose ceiling is sky: #31
 * acceptance criterion 4 asks for the surface to read as daylight, and daylight
 * is not a dim room.
 */
export const DAYLIGHT = 1;

/**
 * Luminance at which two annealed palette entries are still distinguishable at
 * 1 px, as `tests/lighting.test.ts` measures it.
 *
 * docs/03 §6 spaced material colours so the nearest pair in any one biome sits
 * at 34 units of redmean distance. Attenuation scales that distance very nearly
 * linearly, and the review's own reading is that "under about 40 two cells are a
 * coin flip" with the low thirties as the floor worth defending. 0.58 keeps the
 * worst pair at 34 × 0.58 ≈ 20 — the number the separation test asserts, and the
 * reason this constant is 0.58 rather than a round 0.6.
 *
 * This is a floor on the *rendered* luminance — ambient plus incident — not on
 * ambient. That distinction is the whole design: the deep bands are allowed to
 * have a dark floor precisely because their emitters carry them over this line.
 */
export const LEGIBLE_LUMINANCE = 0.58;

/** Where the sky stops mattering: full daylight at or above this. */
const SKY_TOP = SURFACE_Y;

/**
 * Ambient at a biome, by id. Unknown ids fall back to the deepest spine value
 * rather than to daylight — a missing biome should read as "somewhere dark and
 * unfinished", which is loud, not as "the surface", which is silent.
 */
export function biomeAmbient(id: string): number {
  const b = biomeById(id);
  return b === undefined ? 0.34 : b.ambientLight;
}

/** Control point: the world y at which a band's own ambient is exact. */
function bandCentre(band: number): number {
  return band * BAND_HEIGHT + (BAND_HEIGHT >> 1);
}

let ladder: number[] | null = null;

/** The spine's ambient values, top to bottom. Cached; the roster is static. */
function spineAmbients(): number[] {
  if (ladder === null) ladder = MAIN_PATH.map((id) => biomeAmbient(id));
  return ladder;
}

/**
 * Ambient light floor at a world depth.
 *
 * Above the ground line this is `DAYLIGHT` flat — there is no rock overhead, so
 * blending the first band's floor into the sky would make the opening screen
 * dimmer the further west you walk, which is nonsense. Below it, the value
 * interpolates between band centres and clamps at the deepest band, so the
 * descent is a continuous fade rather than eight steps.
 */
export function ambientAt(worldY: number): number {
  const a = spineAmbients();
  // The dressing's daylight — docs/27 §12 item 5. An overcast or fogbound day
  // dims the whole sky branch AND the shaft blend's daylight end, so the
  // curve stays continuous at the mouth whatever the day is doing; a step at
  // y = 0 would be the rendering bug BiomeDef.ambientLight's docblock names.
  const day = DAYLIGHT * surfaceMul;
  if (worldY <= SKY_TOP) return day;

  const first = bandCentre(0);
  if (worldY <= first) {
    // Sky to the first band's interior. The player walks down out of daylight
    // into the top of Rotwood Reach over the length of the descent shaft. The
    // underground mood scales the band end only — `day` already carries the
    // surface dressing's own multiplier, and the two must not compound.
    const t = (worldY - SKY_TOP) / (first - SKY_TOP);
    return day + (a[0] * undergroundMul - day) * t;
  }

  const last = a.length - 1;
  if (worldY >= bandCentre(last)) return a[last] * undergroundMul;

  const band = Math.min(last - 1, Math.floor((worldY - (BAND_HEIGHT >> 1)) / BAND_HEIGHT));
  const y0 = bandCentre(band);
  const t = (worldY - y0) / BAND_HEIGHT;
  return (a[band] + (a[band + 1] - a[band]) * t) * undergroundMul;
}

/**
 * The surface dressing's daylight multiplier — `surfaceDressing().ambient`,
 * applied by `game.ts` when the profile binds. Module state for the cached
 * ladder's reason: `ambientAt` is called per frame from several sites and
 * threading a parameter through all of them would put the same value in
 * every signature.
 */
let surfaceMul = 1;

export function setSurfaceAmbientMul(mul: number): void {
  surfaceMul = mul;
}

/**
 * The underground dressing's ambient multiplier — `undergroundDressing().ambient`
 * (issue #57), applied by `game.ts` once per run construction. Scales every
 * below-ground branch of `ambientAt`; the daylight branch and the shaft
 * blend's daylight end are the surface dressing's alone, so the two moods
 * never compound at the mouth.
 */
let undergroundMul = 1;

export function setUndergroundAmbientMul(mul: number): void {
  undergroundMul = mul;
}

/**
 * Test-only: drop the cached ladder so a re-registered biome roster is seen.
 */
export function _resetLighting(): void {
  ladder = null;
  surfaceMul = 1;
  undergroundMul = 1;
}
